export type HudTransitionMode = 'radial' | 'scanner' | 'route' | 'lock';

export const hudPaths = {
  home: '/',
  serviceArchitecture: '/service-architecture',
  automationSystems: '/ai-automation-systems',
  workflowPipeline: '/workflow-delivery-pipeline',
  verifiedOperations: '/verified-operations',
  communicationsTerminal: '/communications-terminal',
  termsOfService: '/resources/terms-of-service',
  privacyPolicy: '/resources/privacy-policy',
  cookiePolicy: '/resources/cookie-policy',
  promotionTerms: '/resources/promotion-terms-and-conditions'
} as const;

export type HudPath = (typeof hudPaths)[keyof typeof hudPaths];
export const hudBasePath = '/central-command';

export const hudPathOrder: HudPath[] = [
  hudPaths.home,
  hudPaths.serviceArchitecture,
  hudPaths.automationSystems,
  hudPaths.workflowPipeline,
  hudPaths.verifiedOperations,
  hudPaths.communicationsTerminal,
  hudPaths.termsOfService,
  hudPaths.privacyPolicy,
  hudPaths.cookiePolicy,
  hudPaths.promotionTerms
];

export const toHudHash = (path: HudPath): string => (path === hudPaths.home ? '#/' : `#${path}`);
export const toHudCanonicalUrl = (path: HudPath): string =>
  path === hudPaths.home ? `${hudBasePath}/` : `${hudBasePath}/#${path}`;

export const hudModules = [
  {
    id: 'start-panel',
    path: hudPaths.home,
    label: 'Start Panel',
    shortLabel: 'Start',
    summary: 'Root command deck and navigation hub for all operating modules.',
    mode: 'radial'
  },
  {
    id: 'service-architecture',
    path: hudPaths.serviceArchitecture,
    label: 'Service Architecture',
    shortLabel: 'Services',
    summary: 'System architecture tracks for build, infrastructure, and optimization programs.',
    mode: 'route'
  },
  {
    id: 'automation-systems',
    path: hudPaths.automationSystems,
    label: 'AI + Automation Systems',
    shortLabel: 'AI Systems',
    summary: 'Operational AI channels for workflow automation and business process acceleration.',
    mode: 'lock'
  },
  {
    id: 'workflow-pipeline',
    path: hudPaths.workflowPipeline,
    label: 'Workflow / Delivery Pipeline',
    shortLabel: 'Workflow',
    summary: 'Milestone-controlled delivery pipeline from discovery through launch and support.',
    mode: 'scanner'
  },
  {
    id: 'verified-operations',
    path: hudPaths.verifiedOperations,
    label: 'Verified Operations',
    shortLabel: 'Trust',
    summary: 'Operational assurance, quality gates, and accountable delivery systems.',
    mode: 'route'
  },
  {
    id: 'communications-terminal',
    path: hudPaths.communicationsTerminal,
    label: 'Communications Terminal',
    shortLabel: 'Comms',
    summary: 'Secure command channel for project transmission and technical response.',
    mode: 'radial'
  }
] as const satisfies ReadonlyArray<{
  id: string;
  path: HudPath;
  label: string;
  shortLabel: string;
  summary: string;
  mode: HudTransitionMode;
}>;

export const hudPageMeta: Record<HudPath, { shortLabel: string; mode: HudTransitionMode }> = {
  [hudPaths.home]: { shortLabel: 'Start', mode: 'radial' },
  [hudPaths.serviceArchitecture]: { shortLabel: 'Services', mode: 'route' },
  [hudPaths.automationSystems]: { shortLabel: 'AI Systems', mode: 'lock' },
  [hudPaths.workflowPipeline]: { shortLabel: 'Workflow', mode: 'scanner' },
  [hudPaths.verifiedOperations]: { shortLabel: 'Trust', mode: 'route' },
  [hudPaths.communicationsTerminal]: { shortLabel: 'Comms', mode: 'radial' },
  [hudPaths.termsOfService]: { shortLabel: 'Terms', mode: 'lock' },
  [hudPaths.privacyPolicy]: { shortLabel: 'Privacy', mode: 'scanner' },
  [hudPaths.cookiePolicy]: { shortLabel: 'Cookie', mode: 'route' },
  [hudPaths.promotionTerms]: { shortLabel: 'Promo', mode: 'lock' }
};

export const hudCopy = {
  startPanel: {
    heading: 'Primary Command Deck',
    subtitle: 'Root operating screen for DeveLogic Digital. Activate subsystem modules to inspect specific capabilities.',
    mission:
      'DeveLogic Digital engineers production-grade software systems, AI-enabled automations, and resilient delivery operations for real business outcomes.',
    deckNotes: [
      'Unified HUD interface across all subsystem screens',
      'Direct command routing into specialized module pages',
      'Business-critical status visibility from first interaction'
    ],
    capabilitySummary: [
      { label: 'Software Engineering', value: 'Production Ready' },
      { label: 'Architecture Programs', value: 'Modular + Scalable' },
      { label: 'Automation Layer', value: 'Operational Grade' },
      { label: 'Delivery Control', value: 'Milestone Verified' }
    ]
  },
  hero: {
    badge: 'DL-OS // COMMAND SESSION ACTIVE',
    heading: 'Engineering Advanced Software Systems For Real Business Operations',
    body:
      'DeveLogic Digital builds custom software, web and mobile products, infrastructure, and AI-enabled workflows with production-grade delivery discipline.',
    ctaPrimary: 'Open Communications Terminal',
    ctaSecondary: 'Open Service Architecture Console',
    metrics: [
      { label: 'Delivery Mode', value: 'Milestone Controlled' },
      { label: 'Response SLA', value: '< 1 Business Day' },
      { label: 'AI Integration', value: 'Operational Grade' },
      { label: 'Infrastructure', value: 'Migration Ready' }
    ]
  },
  coreSystems: {
    heading: 'Core Capability Matrix',
    subtitle: 'High-level engineering capability summary available from the Start Panel.',
    modules: [
      {
        title: 'Custom Software Engineering',
        body: 'Production-ready software aligned to business constraints, maintainability, and long-term velocity.'
      },
      {
        title: 'Web + Mobile Applications',
        body: 'Connected digital experiences across web and mobile with robust backend architecture.'
      },
      {
        title: 'Backend + API Platforms',
        body: 'Secure service layers and integration-ready APIs for dependable operations.'
      },
      {
        title: 'Optimization + Analytics',
        body: 'Performance instrumentation, diagnostics, and measurable improvement loops.'
      }
    ]
  },
  architecture: {
    heading: 'Service Architecture',
    subtitle: 'Technical programs that combine hosting, integration, migration, and reliability engineering.',
    tracks: [
      {
        label: 'Build Program',
        items: ['Discovery', 'System Design', 'Implementation', 'Integration', 'Production Launch']
      },
      {
        label: 'Infrastructure Program',
        items: ['Hosting Strategy', 'Migration Execution', 'Resilience Hardening', 'Monitoring']
      },
      {
        label: 'Optimization Program',
        items: ['Telemetry Setup', 'Performance Tuning', 'Analytics Review', 'Iteration Roadmap']
      }
    ]
  },
  automation: {
    heading: 'AI + Automation Systems',
    subtitle: 'Practical AI integrated into real operating workflows, not isolated prototypes.',
    modules: [
      {
        title: 'Workflow Automation',
        stat: '82%',
        body: 'Automate repetitive tasks and route operational events across connected systems.'
      },
      {
        title: 'Document Intelligence',
        stat: '4.3x',
        body: 'Classify, summarize, and route high-volume business documents with oversight.'
      },
      {
        title: 'Support Acceleration',
        stat: '63%',
        body: 'AI-assisted triage and response tooling for faster service operations.'
      }
    ]
  },
  pipeline: {
    heading: 'Workflow / Delivery Pipeline',
    subtitle: 'Transparent execution from first briefing to launch and operational support.',
    stages: [
      {
        title: 'Discover',
        tag: 'Context Capture',
        body: 'Map goals, constraints, technical baseline, and business priorities.'
      },
      {
        title: 'Architect',
        tag: 'Blueprint Locked',
        body: 'Define architecture, milestones, dependencies, and delivery sequencing.'
      },
      {
        title: 'Build',
        tag: 'Incremental Release',
        body: 'Ship in controlled milestones with integrated quality and verification gates.'
      },
      {
        title: 'Operate',
        tag: 'Continuous Improvement',
        body: 'Monitor, optimize, and evolve systems against measurable targets.'
      }
    ]
  },
  trust: {
    heading: 'Verified Operations',
    subtitle: 'Trusted delivery through accountable teams, clear communication, and quality controls.',
    checks: [
      'Dedicated project management ownership',
      'Senior engineering oversight',
      'Quality gates before release',
      'Structured progress reporting',
      'Operational support continuity'
    ],
    stats: [
      { label: 'Delivery Visibility', value: 'Real-Time' },
      { label: 'Risk Monitoring', value: 'Continuous' },
      { label: 'System Reliability', value: 'High' }
    ]
  },
  contact: {
    heading: 'Communications Terminal',
    subtitle: 'Transmit project requirements and receive a focused technical response.',
    body: 'Secure submission. Confidential handling. Typical response within one business day.',
    endpoint: '/api/contact'
  },
  microLabels: {
    online: 'SYSTEM ONLINE',
    nominal: 'OPS HEALTH: NOMINAL',
    routeStable: 'ROUTE LINK STABLE',
    signal: 'SIGNAL QUALITY: HIGH',
    verified: 'TRUST LAYER VERIFIED'
  },
  footer: {
    title: 'DeveLogic Digital',
    summary:
      'Advanced software engineering and AI-enabled delivery for teams building high-performance digital systems.',
    resources: [
      {
        label: 'Terms of Service',
        href: toHudHash(hudPaths.termsOfService)
      },
      {
        label: 'Privacy Policy',
        href: toHudHash(hudPaths.privacyPolicy)
      },
      {
        label: 'Cookie Policy',
        href: toHudHash(hudPaths.cookiePolicy)
      },
      {
        label: "Promo T's & C's",
        href: toHudHash(hudPaths.promotionTerms)
      }
    ],
    social: [
      { label: 'Facebook', href: 'https://www.facebook.com/DeveLogicDigital' },
      { label: 'Instagram', href: 'https://www.instagram.com/develogic.digital' },
      { label: 'LinkedIn', href: 'https://www.linkedin.com/company/develogic-software/' },
      { label: 'WhatsApp', href: 'https://wa.me/message/B5V6X3M7RLF6N1' }
    ]
  }
} as const;
